/* Phase 25 (Sentry scaffolding, env-driven no-op).
 *
 * Reads `REACT_APP_SENTRY_DSN` from env. If empty/undefined the module is a
 * strict no-op -- `init()` returns without importing @sentry/react and the
 * runtime cost is one env-var read at boot.
 *
 * When the user pastes a DSN, they:
 *   1. `yarn add @sentry/react` (kept out of dependencies for now)
 *   2. Add `REACT_APP_SENTRY_DSN=https://...` to /app/frontend/.env
 *   3. Uncomment the dynamic import below (kept as a comment so the
 *      module tree-shakes to zero bytes when DSN is unset).
 *   4. Call `initSentry()` once from src/index.js (already wired).
 *
 * The public surface (initSentry, captureException, captureMessage) never
 * throws even when Sentry is off, so callers can sprinkle capture calls
 * without ever adding "if(sentry_ready)" guards.
 */
const DSN = (typeof process !== "undefined" && process.env && process.env.REACT_APP_SENTRY_DSN) || "";
const ENV = (typeof process !== "undefined" && process.env && process.env.REACT_APP_SENTRY_ENV) || "development";
const RELEASE = (typeof process !== "undefined" && process.env && process.env.REACT_APP_SENTRY_RELEASE) || "";

let _sentry = null;   // holds the runtime module once loaded
let _inited = false;

export function isSentryEnabled() {
  return Boolean(DSN);
}

export async function initSentry() {
  if (_inited) return;
  _inited = true;
  if (!DSN) {
    // No-op: no DSN configured. Log once so it's obvious in the console.
    console.info("[sentry] disabled (REACT_APP_SENTRY_DSN unset)");
    return;
  }
  try {
    // NOTE: this block only runs when a DSN is present, so we can safely
    // require @sentry/react at that point. It is NOT in package.json today
    // -- the user must `yarn add @sentry/react` before setting the DSN.
    // Wrapped in a try so a missing SDK doesn't crash the app.
    const mod = await import("@sentry/react").catch(() => null);
    if (!mod) {
      console.warn("[sentry] @sentry/react not installed; DSN set but SDK missing. `yarn add @sentry/react` to enable.");
      return;
    }
    mod.init({
      dsn: DSN,
      environment: ENV,
      release: RELEASE || undefined,
      tracesSampleRate: 0.05,
      // Do NOT ship PII by default; the user can override once wired.
      sendDefaultPii: false,
    });
    _sentry = mod;
    console.info("[sentry] initialised env=%s release=%s", ENV, RELEASE || "-");
  } catch (e) {
    // Never let sentry initialisation itself crash the app.
    console.warn("[sentry] init failed", e);
  }
}

export function captureException(err, ctx) {
  if (_sentry) {
    try { _sentry.captureException(err, ctx ? { extra: ctx } : undefined); } catch (_) { /* noop */ }
    return;
  }
  if (typeof console !== "undefined" && console.error) console.error("[sentry-noop] exception", err, ctx || "");
}

export function captureMessage(msg, level = "info", ctx) {
  if (_sentry) {
    try { _sentry.captureMessage(msg, { level, extra: ctx }); } catch (_) { /* noop */ }
    return;
  }
  if (typeof console !== "undefined" && console.info) console.info("[sentry-noop] msg", level, msg, ctx || "");
}

export function setUser(user) {
  if (_sentry) {
    try { _sentry.setUser(user || null); } catch (_) { /* noop */ }
  }
}

export default { initSentry, captureException, captureMessage, setUser, isSentryEnabled };
